export type TransferSyntaxValue = 'accepted' | 'rejected';

export interface AbstractSyntaxConfig {
  explicitVrBigEndian: TransferSyntaxValue;
  explicitVrLittleEndian: TransferSyntaxValue;
  implicitVrLittleEndian: TransferSyntaxValue;
  jpeg2000ImageCompression: TransferSyntaxValue;
  jpeg2000ImageCompressionLosslessOnly: TransferSyntaxValue;
  jpegBaselineProcess1: TransferSyntaxValue;
  jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1: TransferSyntaxValue;
  jpegLsLosslessImageCompression: TransferSyntaxValue;
  jpegLsLossyNearLosslessImageCompression: TransferSyntaxValue;
  mpeg4AvcH264HighProfile: TransferSyntaxValue;
}

export interface DicomSyntaxConfig {
  secondaryCaptureImageStorage: AbstractSyntaxConfig;
  ultrasoundImageStorage: AbstractSyntaxConfig;
  ultrasoundMultiFrameImageStorage: AbstractSyntaxConfig;
  verificationSopClass: Partial<AbstractSyntaxConfig>;
}

export interface ManufacturerModel {
  modelName: string;
}

export interface DefaultManufacturer {
  manufacturerName: string;
  models: ManufacturerModel[];
}

export interface DefaultTransferSyntax {
  abstractSyntax: string;
  abstractSyntaxName: string;
  acceptedTransferSyntaxes: string[];
}

export interface DefaultAbstractSyntaxConfig {
  explicitVrBigEndian: TransferSyntaxValue;
  explicitVrLittleEndian: TransferSyntaxValue;
  implicitVrLittleEndian: TransferSyntaxValue;
  jpeg2000ImageCompression: TransferSyntaxValue;
  jpeg2000ImageCompressionLosslessOnly: TransferSyntaxValue;
  jpegBaselineProcess1: TransferSyntaxValue;
  jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1: TransferSyntaxValue;
  jpegLsLosslessImageCompression: TransferSyntaxValue;
  jpegLsLossyNearLosslessImageCompression: TransferSyntaxValue;
  mpeg4AvcH264HighProfile: TransferSyntaxValue;
}

export interface DefaultDicomSyntaxConfig {
  secondaryCaptureImageStorage: DefaultAbstractSyntaxConfig;
  ultrasoundImageStorage: DefaultAbstractSyntaxConfig;
  ultrasoundMultiFrameImageStorage: DefaultAbstractSyntaxConfig;
  verificationSopClass: Partial<DefaultAbstractSyntaxConfig>;
}

export interface AeDefaultConfiguration {
  aeNamePrefix: string;
  aeTitlePrefix: string;
  deviceId?: number;
  defaultManufacturers: DefaultManufacturer[];
  defaultTransferSyntaxes: DefaultTransferSyntax[];
  defaultSyntaxConfig: DefaultDicomSyntaxConfig;
}

// Manufacturer and Model data
export const MANUFACTURER_MODEL_DATA = {
  'Butterfly Network': [
    { label: 'Butterfly iQ (original)', value: 'Butterfly iQ (original)' },
    { label: 'Butterfly iQ+', value: 'Butterfly iQ+' },
    { label: 'Butterfly iQ+ Bladder', value: 'Butterfly iQ+ Bladder' },
    { label: 'Butterfly iQ3', value: 'Butterfly iQ3' },
    { label: 'Butterfly iQ Vet', value: 'Butterfly iQ Vet' },
    { label: 'Butterfly iQ+ Vet', value: 'Butterfly iQ+ Vet' },
  ],
  'GE HealthCare': [
    { label: 'Vscan Air CL', value: 'Vscan Air CL' },
    { label: 'Vscan Air SL', value: 'Vscan Air SL' },
  ],
};

export const MANUFACTURER_OPTIONS = Object.keys(MANUFACTURER_MODEL_DATA).map(
  (manufacturer) => ({
    label: manufacturer,
    value: manufacturer,
  })
);

// Default configurations
export const defaultSyntaxConfig: DicomSyntaxConfig = {
  secondaryCaptureImageStorage: {
    explicitVrBigEndian: 'accepted',
    explicitVrLittleEndian: 'accepted',
    implicitVrLittleEndian: 'accepted',
    jpeg2000ImageCompression: 'accepted',
    jpeg2000ImageCompressionLosslessOnly: 'accepted',
    jpegBaselineProcess1: 'accepted',
    jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
      'accepted',
    jpegLsLosslessImageCompression: 'accepted',
    jpegLsLossyNearLosslessImageCompression: 'accepted',
    mpeg4AvcH264HighProfile: 'accepted',
  },
  ultrasoundImageStorage: {
    explicitVrBigEndian: 'accepted',
    explicitVrLittleEndian: 'accepted',
    implicitVrLittleEndian: 'accepted',
    jpeg2000ImageCompression: 'accepted',
    jpeg2000ImageCompressionLosslessOnly: 'accepted',
    jpegBaselineProcess1: 'accepted',
    jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
      'accepted',
    jpegLsLosslessImageCompression: 'accepted',
    jpegLsLossyNearLosslessImageCompression: 'accepted',
    mpeg4AvcH264HighProfile: 'accepted',
  },
  ultrasoundMultiFrameImageStorage: {
    explicitVrBigEndian: 'accepted',
    explicitVrLittleEndian: 'accepted',
    implicitVrLittleEndian: 'accepted',
    jpeg2000ImageCompression: 'accepted',
    jpeg2000ImageCompressionLosslessOnly: 'accepted',
    jpegBaselineProcess1: 'accepted',
    jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
      'accepted',
    jpegLsLosslessImageCompression: 'accepted',
    jpegLsLossyNearLosslessImageCompression: 'accepted',
    mpeg4AvcH264HighProfile: 'accepted',
  },
  verificationSopClass: {
    explicitVrBigEndian: 'accepted',
    explicitVrLittleEndian: 'accepted',
    implicitVrLittleEndian: 'accepted',
  },
};

// Abstract Syntax metadata
export const abstractSyntaxInfo = {
  secondaryCaptureImageStorage: {
    name: 'Secondary Capture Image Storage',
    sopClassUid: '1.2.840.10008.5.1.4.1.1.7',
    description:
      'Storage of secondary capture images (screenshots, derived images)',
    securityNote: 'Default policy: All transfer syntaxes rejected',
  },
  ultrasoundImageStorage: {
    name: 'Ultrasound Image Storage',
    sopClassUid: '1.2.840.10008.5.1.4.1.1.6.1',
    description: 'Storage of ultrasound images',
    securityNote: 'Default policy: Most transfer syntaxes accepted',
  },
  ultrasoundMultiFrameImageStorage: {
    name: 'Ultrasound Multi-frame Image Storage',
    sopClassUid: '1.2.840.10008.5.1.4.1.1.3.1',
    description: 'Storage of multi-frame ultrasound images',
    securityNote: 'Default policy: Most transfer syntaxes accepted',
  },
  verificationSopClass: {
    name: 'Verification SOP Class',
    sopClassUid: '1.2.840.10008.1.1',
    description: 'DICOM verification and echo services',
    securityNote: 'Default policy: Only implicit VR little endian accepted',
  },
};

// Default configuration for AeDefaultConfiguration
export const defaultDefaultSyntaxConfig: DefaultDicomSyntaxConfig = {
  secondaryCaptureImageStorage: {
    explicitVrBigEndian: 'accepted',
    explicitVrLittleEndian: 'accepted',
    implicitVrLittleEndian: 'accepted',
    jpeg2000ImageCompression: 'accepted',
    jpeg2000ImageCompressionLosslessOnly: 'accepted',
    jpegBaselineProcess1: 'accepted',
    jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
      'accepted',
    jpegLsLosslessImageCompression: 'accepted',
    jpegLsLossyNearLosslessImageCompression: 'accepted',
    mpeg4AvcH264HighProfile: 'accepted',
  },
  ultrasoundImageStorage: {
    explicitVrBigEndian: 'accepted',
    explicitVrLittleEndian: 'accepted',
    implicitVrLittleEndian: 'accepted',
    jpeg2000ImageCompression: 'accepted',
    jpeg2000ImageCompressionLosslessOnly: 'accepted',
    jpegBaselineProcess1: 'accepted',
    jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
      'accepted',
    jpegLsLosslessImageCompression: 'accepted',
    jpegLsLossyNearLosslessImageCompression: 'accepted',
    mpeg4AvcH264HighProfile: 'accepted',
  },
  ultrasoundMultiFrameImageStorage: {
    explicitVrBigEndian: 'accepted',
    explicitVrLittleEndian: 'accepted',
    implicitVrLittleEndian: 'accepted',
    jpeg2000ImageCompression: 'accepted',
    jpeg2000ImageCompressionLosslessOnly: 'accepted',
    jpegBaselineProcess1: 'accepted',
    jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
      'accepted',
    jpegLsLosslessImageCompression: 'accepted',
    jpegLsLossyNearLosslessImageCompression: 'accepted',
    mpeg4AvcH264HighProfile: 'accepted',
  },
  verificationSopClass: {
    explicitVrBigEndian: 'accepted',
    explicitVrLittleEndian: 'accepted',
    implicitVrLittleEndian: 'accepted',
  },
};

// Transfer Syntax metadata
export const transferSyntaxInfo = {
  explicitVrBigEndian: {
    name: 'Explicit VR Big Endian',
    uid: '1.2.840.10008.1.2.2',
    type: 'Uncompressed',
  },
  explicitVrLittleEndian: {
    name: 'Explicit VR Little Endian',
    uid: '1.2.840.10008.1.2.1',
    type: 'Uncompressed',
  },
  implicitVrLittleEndian: {
    name: 'Implicit VR Little Endian',
    uid: '1.2.840.10008.1.2',
    type: 'Uncompressed',
  },
  jpeg2000ImageCompression: {
    name: 'JPEG 2000 Image Compression',
    uid: '1.2.840.10008.1.2.4.91',
    type: 'Compressed',
  },
  jpeg2000ImageCompressionLosslessOnly: {
    name: 'JPEG 2000 Part 1 Lossless Only',
    uid: '1.2.840.10008.1.2.4.90',
    type: 'Compressed',
  },
  jpegBaselineProcess1: {
    name: 'JPEG Baseline Process 1',
    uid: '1.2.840.10008.1.2.4.50',
    type: 'Compressed',
  },
  jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1: {
    name: 'JPEG Lossless, Non-Hierarchical',
    uid: '1.2.840.10008.1.2.4.70',
    type: 'Compressed',
  },
  jpegLsLosslessImageCompression: {
    name: 'JPEG-LS Lossless Image Compression',
    uid: '1.2.840.10008.1.2.4.80',
    type: 'Compressed',
  },
  jpegLsLossyNearLosslessImageCompression: {
    name: 'JPEG-LS Lossy (Near-Lossless)',
    uid: '1.2.840.10008.1.2.4.81',
    type: 'Compressed',
  },
  mpeg4AvcH264HighProfile: {
    name: 'MPEG-4 AVC/H.264 High Profile / Level 4.1',
    uid: '1.2.840.10008.1.2.4.102',
    type: 'Compressed',
  },
};
